// Skip unrendered elements: a hidden trap boundary lets focus escape.
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => element.getClientRects().length > 0);
}

class SectionId {
  static #separator = '__';

  // Qualified section ids look like 'template--22224696705326__main'.
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    // Restore scroll on the PDP swap only; the quick-add modal's page is scroll-locked.
    const isQuickAdd = !!oldNode.closest('quick-add-modal');
    const scrollY = window.scrollY;

    // Hide the old node first so the new one never measures against a shared flex row.
    oldNode.style.display = 'none';
    oldNode.parentNode.insertBefore(newNode, oldNode);
    if (!isQuickAdd) window.scrollTo(0, scrollY);

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Reinjects script tags, since innerHTML leaves them inert.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return;
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

// Close only on a downward swipe that began with the sheet already scrolled to the top.
function bindSwipeDownToClose(captureElement, scrollElement, closeFn) {
  const mobileQuery = window.matchMedia('(max-width: 768px)');
  let startX = 0;
  let startY = 0;
  let startedAtTop = false;

  captureElement.addEventListener('touchstart', (event) => {
    startX = event.touches[0].clientX;
    startY = event.touches[0].clientY;
    startedAtTop = !scrollElement || scrollElement.scrollTop <= 0;
  });

  captureElement.addEventListener('touchend', (event) => {
    if (!mobileQuery.matches) return;
    if (!startedAtTop) return;
    if (scrollElement && scrollElement.scrollTop > 0) return;

    const deltaX = event.changedTouches[0].clientX - startX;
    const deltaY = event.changedTouches[0].clientY - startY;
    const minDistance = 75;
    if (deltaY < minDistance || Math.abs(deltaY) < 1.5 * Math.abs(deltaX)) return;

    closeFn();
  });
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}


function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 990px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    // The cart drawer is rendered from theme.liquid, outside any section.
    const section = this.closest('.shopify-section');
    if (section) this.dataset.section = section.id.replace('shopify-section-', '');
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute('style');
      deferredElement.setAttribute('style', 'display: block;');
      window.setTimeout(() => {
        deferredElement.setAttribute('style', formerStyle);
      }, 0);
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes (Shopify/dawn#2057).
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId
    );
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (this.classList.contains('complementary-products') && !recommendations?.innerHTML.trim().length) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      event.target.setAttribute('value', inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance"

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      startMarker.name,
      `${metricName}:end`
    );
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}


function setHeaderHeight() {
  const header = document.querySelector(".header-main");
  if (!header) return;
  const value = header.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--header-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--header-height", `${value}px`);
  }
}

function setHeaderTop() {
  const header = document.querySelector(".header-main");
  if (!header) return;

  const value = header.getBoundingClientRect().top;
  let currentValue = getComputedStyle(document.documentElement).getPropertyValue("--header-top-position").trim();

  const currentNumeric = parseFloat(currentValue || NaN);

  if (!Number.isFinite(currentNumeric) || currentNumeric !== value) {
    document.documentElement.style.setProperty("--header-top-position", `${value}px`);
  }
}

function setHeaderBottom() {
  const header = document.querySelector(".header-main");
  if (!header) return;
  const value = header.getBoundingClientRect().bottom;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--header-bottom-position");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--header-bottom-position", `${value}px`);
  }
}

function setPromoHeight() {
  const promo = document.querySelector(".announcement-bar");
  // No bar is a real height of 0, not a reason to keep the last value.
  if (!promo) {
    document.documentElement.style.setProperty("--promo-height", "0px");
    return;
  }
  const value = promo.getBoundingClientRect().height;
  const currentValue = getComputedStyle(
    document.documentElement
  ).getPropertyValue("--promo-height");

  if (value && value != currentValue) {
    document.documentElement.style.setProperty("--promo-height", `${value}px`);
  }
}

function setCustomPropertiesOnLoad() {
  setHeaderHeight();
  setPromoHeight();
  setHeaderTop();
  setHeaderBottom();
}

function setCustomPropertiesOnScroll() {
  setHeaderTop();
  setHeaderBottom();
}

function setCustomPropertiesOnResize() {
  setHeaderHeight();
  setPromoHeight();
  setHeaderTop();
  setHeaderBottom();
}

window.addEventListener("load", setCustomPropertiesOnLoad, false);

window.addEventListener("scroll", debounce(setCustomPropertiesOnScroll, 300), false);

window.addEventListener("resize", debounce(setCustomPropertiesOnResize, 300), false);

class HeaderMega extends HTMLElement {
  constructor() {
    super();

    this.dropdowns = this.querySelectorAll(".header-dropdown");
    this.dropdownToggles = this.querySelectorAll(".header-dropdown-toggle");
    this.dropdownMenus = this.querySelectorAll(".header-dropdown-menu");
    this.menuTrigger = this.getAttribute("trigger");

    this._openTimers = new Map();
    this._closeTimers = new Map();
    this.hoverDelay = 100;

    this.bindEvents();
  }

  bindEvents() {
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onDropdownFocusOut.bind(this));

    this.bindClickEvents();
    if (this.menuTrigger === "hover") {
      this.bindHoverEvents();
    }
  }

  /** CLICK EVENTS **/
  bindClickEvents() {
    this.dropdownToggles.forEach((item) => {
      item.addEventListener("click", this.onDropdownToggleClick.bind(this));
    });
  }

  /** HOVER EVENTS **/
  bindHoverEvents() {
    this.dropdownToggles.forEach((toggle) => {
      const dropdown = toggle.parentNode;
      const menu = dropdown.querySelector(".header-dropdown-menu");

      toggle.addEventListener("mouseenter", () =>
        this.handleHoverEnter(dropdown)
      );
      toggle.addEventListener("mouseleave", () =>
        this.handleHoverLeave(dropdown)
      );

      if (menu) {
        menu.addEventListener("mouseenter", () =>
          this.handleHoverEnter(dropdown)
        );
        menu.addEventListener("mouseleave", () =>
          this.handleHoverLeave(dropdown)
        );
      }
    });
  }

  handleHoverEnter(dropdown) {
    clearTimeout(this._closeTimers.get(dropdown));
    const timer = setTimeout(() => {
      this.openDropdown(dropdown);
      this.openMega();
    }, this.hoverDelay);
    this._openTimers.set(dropdown, timer);
  }

  handleHoverLeave(dropdown) {
    clearTimeout(this._openTimers.get(dropdown));
    const timer = setTimeout(() => {
      this.closeDropdown(dropdown);
      this.closeMega();
    }, this.hoverDelay);
    this._closeTimers.set(dropdown, timer);
  }

  onDropdownToggleClick(event) {
    event.preventDefault();

    const dropdownToggle = event.currentTarget;
    const dropdown = dropdownToggle.parentNode;

    if (this.isDropdownOpen(dropdown)) {
      this.closeDropdown(dropdown);
      this.closeMega();
    } else {
      this.openDropdown(dropdown);
      this.openMega();
    }
  }

  onDropdownFocusOut(event) {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      if (this.hasDropdownOpen()) {
        this.closeAllDropdowns();
        this.closeMega();
      }
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    this.closeDropdown(openDetailsElement);
    this.closeMega();
  }

  isDropdownOpen(detailsElement) {
    return (
      detailsElement.hasAttribute("open")
    );
  }

  isDropdownClose(detailsElement) {
    return !detailsElement.hasAttribute("open");
  }

  hasDropdownOpen() {
    return this.classList.contains("dropdown-open");
  }

  openDropdown(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");

    this.closeAllDropdowns();

    detailsElement.setAttribute("open", "");
    summaryElement.setAttribute("aria-expanded", true);
    summaryElement.classList.add("active");
    summaryElement.classList.add("link--underline");
    this.classList.add("dropdown-open");
    trapFocus(detailsElement, summaryElement);
  }

  closeDropdown(detailsElement) {
    const summaryElement = detailsElement.querySelector("summary");
    // Keeps the colour swap instant on close; DetailsModal.close has the search equivalent.
    const headerInner = this.closest(".header-inner");
    headerInner?.classList.add("header-mega-closing");

    detailsElement.removeAttribute("open");
    summaryElement.setAttribute("aria-expanded", false);
    summaryElement.classList.remove("active");
    summaryElement.classList.remove("link--underline");
    removeTrapFocus(summaryElement);
    this.classList.remove("dropdown-open");

    if (headerInner) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => headerInner.classList.remove("header-mega-closing"));
      });
    }
  }

  closeAllDropdowns() {
    this.dropdowns.forEach((item) => {
      if (this.isDropdownOpen(item)) {
        this.closeDropdown(item);
      }
    });
  }

  openMega() {
    document.body.classList.add("header-mega-open", "overflow-hidden");
  }

  closeMega() {
    document.body.classList.remove("header-mega-open", "overflow-hidden");
  }
}
customElements.define("header-mega", HeaderMega);

const PRODUCT_CARD_SIMPLE_COLUMNS = '8';

class ProductCardSlider extends HTMLElement {
  connectedCallback() {
    // see decisions.md: data-collection-grid-columns
    const grid = this.closest('[data-collection-grid-columns]');

    if (grid && grid.getAttribute('data-collection-grid-columns') === PRODUCT_CARD_SIMPLE_COLUMNS) {
      this.simple = true;
      this.classList.add('product_card_slider--simple');
      return;
    }

    this.simple = false;
    this.initSwiper();
  }

  syncToColumns(columns) {
    this.setSimpleMode(String(columns) === PRODUCT_CARD_SIMPLE_COLUMNS);
  }

  // Simple mode: first image, second on hover; only the CLP's 8-up view uses it.
  setSimpleMode(simple) {
    if (simple === this.simple) return;

    this.simple = simple;

    if (simple) {
      // cleanStyles drops Swiper's inline heights so CSS can size the card.
      if (this.swiper) this.swiper.destroy(true, true);
      this.swiper = null;
      this.classList.add('product_card_slider--simple');
      return;
    }

    // Rebuild off the click handler: constructing Swipers synchronously stalls the column animation.
    if (this.restoreIdle) cancelProductCardIdle(this.restoreIdle);

    this.restoreIdle = requestProductCardIdle(() => {
      this.restoreIdle = null;

      // Switched back to 8-up while this was queued.
      if (this.simple) return;

      this.classList.remove('product_card_slider--simple');
      this.initSwiper();
    });
  }

  initSwiper() {
    if (this.simple || this.swiper) return;
    if (typeof Swiper === 'undefined') return;

    this.swiper = new Swiper(this, {
      slidesPerView: 1,
      spaceBetween: 0,
      autoHeight: true,
      mousewheel: {
        forceToAxis: true,
      },
      speed: 500,
      resistanceRatio: 0.8,
      touchRatio: 0.8,
      longSwipesRatio: 0.4,
      nested: true,
      navigation: {
        nextEl: this.querySelector('.product_card_slider_next'),
        prevEl: this.querySelector('.product_card_slider_prev'),
      },
      scrollbar: {
        el: this.querySelector('.swiper-scrollbar'),
      },
    });
  }
}
customElements.define('product-card-slider', ProductCardSlider);

// Minimal gallery for quick-add off the PDP; the PDP's section JS defines the full one first (see decisions.md).
document.addEventListener('DOMContentLoaded', () => {
  if (customElements.get('product-gallery-slider')) return;

  customElements.define(
    'product-gallery-slider',
    class extends HTMLElement {
      connectedCallback() {
        if (this.swiper || typeof Swiper === 'undefined') return;
        if (!window.matchMedia('(max-width: 768px)').matches) return;

        this.swiper = new Swiper(this, {
          slidesPerView: 1,
          spaceBetween: 0,
          grabCursor: true,
          scrollbar: { el: this.querySelector('.swiper-scrollbar') },
        });
      }

      disconnectedCallback() {
        this.swiper?.destroy(true, true);
        this.swiper = null;
      }
    }
  );
});

const productCardGalleryCache = {};
const productCardGalleryLoading = {};
const PRODUCT_CARD_MOBILE_QUERY = '(max-width: 768px)';
const PRODUCT_CARD_MOBILE_PRELOAD_DELAY = 2000;
// Long on purpose: only a settled hover should pay for a full-width image.
const PRODUCT_CARD_HERO_PRELOAD_DELAY = 1500;

// Same section main-product's colour preload uses: its URLs are the PDP's.
const PRODUCT_CARD_HERO_PRELOAD_SECTION = 'product-gallery-preload-api';

// Page-wide: two cards can point at the same product.
const productCardHeroWarmed = new Set();

const prefetchUnwelcome = () => {
  const connection = navigator.connection;
  return (
    Boolean(connection) &&
    (connection.saveData === true || /(^|-)[23]g$/.test(connection.effectiveType || ''))
  );
};

const PRODUCT_CARD_SIZE_STEP = 50;
const PRODUCT_CARD_SIZE_DEBOUNCE = 500;

// Safari lacked requestIdleCallback; the timeout stops a busy thread starving it.
const requestProductCardIdle = window.requestIdleCallback
  ? (callback) => window.requestIdleCallback(callback, { timeout: 2000 })
  : (callback) => setTimeout(callback, 300);

const cancelProductCardIdle = window.cancelIdleCallback
  ? (handle) => window.cancelIdleCallback(handle)
  : (handle) => clearTimeout(handle);

class ProductCard extends HTMLElement {
  connectedCallback() {
    this.swatches = this.querySelectorAll('.product_card_swatch');
    this.link = this.querySelector('.product_card_link');
    this.galleryContainer = this.querySelector('.product_card_media_wrapper');

    this.observeCardWidth();
    this.watchHoverIntent();

    if (!this.swatches.length) return;

    const activeSwatch = this.querySelector('.product_card_swatch.active');
    const activeUrl = activeSwatch?.getAttribute('data-product-url');
    if (activeUrl && !productCardGalleryCache[activeUrl]) {
      productCardGalleryCache[activeUrl] = this.galleryContainer.cloneNode(true);
    }

    if (window.matchMedia(PRODUCT_CARD_MOBILE_QUERY).matches) {
      this.revealObserver = new IntersectionObserver((entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.revealTimeout = setTimeout(() => this.preloadSiblingGalleries(), PRODUCT_CARD_MOBILE_PRELOAD_DELAY);
      });
      this.revealObserver.observe(this);
    } else {
      this.addEventListener('mouseenter', () => this.preloadSiblingGalleries(), { once: true });
    }

    this.swatches.forEach((swatch) => {
      ['mouseenter', 'focus', 'touchstart'].forEach((event) => {
        swatch.addEventListener(event, () => this.fetchGallery(swatch), { passive: true });
      });

      swatch.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectSwatch(swatch);
      });
    });
  }

  disconnectedCallback() {
    this.revealObserver?.disconnect();
    this.widthObserver?.disconnect();
    clearTimeout(this.revealTimeout);
    clearTimeout(this.sizeTimeout);
    this.cancelHeroPreload();
    if (this.sizeIdle) cancelProductCardIdle(this.sizeIdle);
  }

  // Write the rendered width back as sizes: the CLP column switcher outgrows Liquid's fixed vw.
  observeCardWidth() {
    if (typeof ResizeObserver === 'undefined') return;

    this.widthObserver = new ResizeObserver((entries) => {
      this.updateImageSizes(entries[0].contentRect.width);
    });

    this.widthObserver.observe(this.galleryContainer || this);
  }

  updateImageSizes(width) {
    // A card in a hidden panel measures 0; keep the server-rendered sizes.
    if (!width) return;

    // Rounded up so a window resize doesn't rewrite it every pixel.
    const sizes = `${Math.ceil(width / PRODUCT_CARD_SIZE_STEP) * PRODUCT_CARD_SIZE_STEP}px`;

    if (sizes === this.imageSizes) return;

    this.imageSizes = sizes;

    // First sizing is immediate; later ones are debounced past the column-switch animation.
    if (!this.hasSizedImages) {
      this.hasSizedImages = true;
      this.applyImageSizes();
      return;
    }

    clearTimeout(this.sizeTimeout);

    this.sizeTimeout = setTimeout(
      () => this.applyImageSizes(),
      PRODUCT_CARD_SIZE_DEBOUNCE
    );
  }

  applyImageSizes() {
    if (!this.imageSizes) return;

    const images = Array.from(this.querySelectorAll('img[srcset]'));

    if (!images.length) return;

    // Hidden slides wait for idle: rewriting them all at once is a long task after a column switch.
    this.setImageSizesOn(images[0]);

    const hidden = images.slice(1);

    if (!hidden.length) return;

    if (this.sizeIdle) cancelProductCardIdle(this.sizeIdle);

    this.sizeIdle = requestProductCardIdle(() => {
      this.sizeIdle = null;
      hidden.forEach((image) => this.setImageSizesOn(image));
    });
  }

  setImageSizesOn(image) {
    if (image.sizes !== this.imageSizes) image.sizes = this.imageSizes;
  }

  // see decisions.md: card hover warms the PDP hero
  watchHoverIntent() {
    // On touch the tap is already navigating, so a warm would compete with it.
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

    this.hoverIntent = true;

    this.addEventListener('pointerenter', () => this.startHeroPreload());
    this.addEventListener('pointerleave', () => this.cancelHeroPreload());

    this.swatches.forEach((swatch) => {
      swatch.addEventListener('pointerenter', () => this.cancelHeroPreload());
      // Leaving the card fires this too; the card's own pointerleave cancels again.
      swatch.addEventListener('pointerleave', () => this.startHeroPreload());
    });
  }

  startHeroPreload() {
    if (!this.hoverIntent) return;

    this.cancelHeroPreload();
    this.heroTimeout = setTimeout(() => this.preloadHero(), PRODUCT_CARD_HERO_PRELOAD_DELAY);
  }

  cancelHeroPreload() {
    clearTimeout(this.heroTimeout);
    this.heroTimeout = null;
  }

  async preloadHero() {
    this.heroTimeout = null;

    // A swatch click rewrites this href, so it always names the colour showing.
    const href = this.link?.getAttribute('href');
    if (!href) return;

    // Drop ?variant=, or one product lands in the warmed set under two keys.
    const productUrl = href.split('?')[0];

    if (productCardHeroWarmed.has(productUrl) || prefetchUnwelcome()) return;

    productCardHeroWarmed.add(productUrl);

    try {
      const response = await fetch(`${productUrl}?section_id=${PRODUCT_CARD_HERO_PRELOAD_SECTION}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const source = doc.querySelector('.product-gallery-item img');
      if (!source) return;

      // Mirror the fetched gallery's sizes/srcset, never this card's.
      const image = new Image();
      if (source.sizes) image.sizes = source.sizes;
      if (source.srcset) image.srcset = source.srcset;
      image.src = source.src;
    } catch (error) {
      // Forget it so a later hover can retry; the PDP loads the image anyway.
      productCardHeroWarmed.delete(productUrl);
    }
  }

  preloadSiblingGalleries() {
    this.swatches.forEach((swatch) => this.fetchGallery(swatch));
  }

  selectSwatch(swatch) {
    if (swatch.classList.contains('active')) return;

    this.requestedSwatch = swatch;
    this.swatches.forEach((item) => item.classList.remove('active'));
    swatch.classList.add('active');

    const productUrl = swatch.getAttribute('data-product-url');
    if (productUrl && this.link) this.link.setAttribute('href', productUrl);

    if (!productCardGalleryCache[productUrl]) {
      this.galleryContainer.classList.add('is-loading');
    }

    // A colour click points the card at another product, so restart the countdown.
    this.startHeroPreload();

    this.fetchGallery(swatch, true);
  }

  async fetchGallery(swatch, shouldUpdate = false) {
    if (!shouldUpdate && swatch.classList.contains('active')) return;

    const productUrl = swatch.getAttribute('data-product-url');
    if (!productUrl) return;

    if (!productCardGalleryCache[productUrl]) {
      if (!productCardGalleryLoading[productUrl]) {
        productCardGalleryLoading[productUrl] = (async () => {
          try {
            const response = await fetch(`${productUrl}?section_id=product-card-gallery-api`);
            const text = await response.text();
            const doc = new DOMParser().parseFromString(text, 'text/html');
            const gallery = doc.querySelector('.product_card_media_wrapper');
            if (gallery) {
              productCardGalleryCache[productUrl] = gallery;

              // Warm the image bytes too, or updateGallery's swap still fetches and flashes.
              gallery.querySelectorAll('img').forEach((sourceImage) => {
                const image = new Image();
                // this.imageSizes, so the warmed URL matches what the swapped-in <img> resolves.
                const sizes = this.imageSizes || sourceImage.sizes;
                if (sizes) image.sizes = sizes;
                if (sourceImage.srcset) image.srcset = sourceImage.srcset;
                image.src = sourceImage.src;
              });
            }
          } finally {
            delete productCardGalleryLoading[productUrl];
          }
        })();
      }

      if (!shouldUpdate) return;
      await productCardGalleryLoading[productUrl];
    }

    if (shouldUpdate && productCardGalleryCache[productUrl] && this.requestedSwatch === swatch) {
      this.updateGallery(productCardGalleryCache[productUrl]);
    }
  }

  updateGallery(gallery) {
    requestAnimationFrame(() => {
      this.galleryContainer.innerHTML = gallery.innerHTML;
      this.galleryContainer.classList.remove('is-loading');
      // Fresh <img>s carry the gallery-api's sizes; see observeCardWidth.
      this.applyImageSizes();
    });
  }
}
customElements.define('product-card', ProductCard);

// see decisions.md: collection-link hover prefetch
const COLLECTION_PREVIEW_SECTION = 'collection-preview-api';

// Steady hover first, so sweeping across a nav row doesn't fire per link.
const COLLECTION_PREVIEW_HOVER_DELAY = 100;

// Per-page ceiling so opening every menu can't queue dozens of requests.
const COLLECTION_PREVIEW_LIMIT = 12;

(() => {
  // Pointer devices only: on touch the tap is already navigating.
  if (!window.matchMedia('(hover: hover)').matches) return;

  const prefetched = new Set();

  let timer = null;
  let pending = null;

  const previewUrl = (link) => {
    // see decisions.md: data-subcollection-link
    if (link.hasAttribute('data-subcollection-link')) return null;

    let url;

    try {
      url = new URL(link.href, window.location.origin);
    } catch (error) {
      return null;
    }

    if (url.origin !== window.location.origin) return null;

    // Substring tests allow a locale prefix; '/products/' excludes long-form PDP links.
    if (!url.pathname.includes('/collections/')) return null;
    if (url.pathname.includes('/products/')) return null;

    // Already here - the grid is on screen.
    if (url.pathname === window.location.pathname) return null;

    url.searchParams.set('section_id', COLLECTION_PREVIEW_SECTION);

    return url.toString();
  };

  const warmImages = (markup) => {
    const preview = new DOMParser().parseFromString(markup, 'text/html');

    preview.querySelectorAll('img').forEach((source) => {
      if (!source.src) return;

      // Mirror sizes and srcset before src, or the warmed URL is not the one the page resolves.
      const image = new Image();

      if (source.sizes) image.sizes = source.sizes;
      if (source.srcset) image.srcset = source.srcset;

      image.src = source.src;
    });
  };

  const prefetch = async (url) => {
    prefetched.add(url);

    try {
      const response = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });

      if (!response.ok) throw new Error(`Request failed: ${response.status}`);

      warmImages(await response.text());
    } catch (error) {
      // Optimisation only: fail quietly and let a later hover retry.
      prefetched.delete(url);
    }
  };

  const cancel = () => {
    clearTimeout(timer);
    timer = null;
    pending = null;
  };

  const schedule = (event) => {
    const link = event.target.closest?.('a[href]');

    if (!link || link === pending) return;

    if (prefetched.size >= COLLECTION_PREVIEW_LIMIT || prefetchUnwelcome()) return;

    const url = previewUrl(link);

    if (!url || prefetched.has(url)) return;

    // A new link under the cursor supersedes one still waiting.
    cancel();

    pending = link;
    timer = setTimeout(() => {
      cancel();
      prefetch(url);
    }, COLLECTION_PREVIEW_HOVER_DELAY);
  };

  // pointerover/out because only they bubble to the delegated listener.
  document.addEventListener('pointerover', schedule, { passive: true });
  document.addEventListener('pointerout', (event) => {
    if (pending && event.target.closest?.('a[href]') === pending) cancel();
  }, { passive: true });

  // Keyboard tabbing through a menu is the same intent.
  document.addEventListener('focusin', schedule);
})();

class FeaturedCollectionSlider extends HTMLElement {
  connectedCallback() {
    if (typeof Swiper === 'undefined') return;

    const header = this.closest('.featured_collection')?.querySelector('.featured_collection_header');

    const totalSlides = Number(this.getAttribute('data-total-slides'));
    const largeCount = totalSlides > 4 ? 5 : 4;

    this.swiper = new Swiper(this, {
      slidesPerView: 2,
      spaceBetween: 16,
      freeMode: true,
      watchOverflow: true,
      mousewheel: {
        forceToAxis: true
      },
      navigation: {
        nextEl: header?.querySelector('.featured_collection_nav_next'),
        prevEl: header?.querySelector('.featured_collection_nav_prev'),
      },
      pagination: {
        el: header?.querySelector('.featured_collection_counter'),
        type: 'fraction',
      },
      breakpoints: {
        1024: {
          slidesPerView: 4,
          spaceBetween: 16,
        },
        2000: {
          slidesPerView: largeCount,
          spaceBetween: 16,
        },
      },
    });
  }
}
customElements.define('featured-collection-slider', FeaturedCollectionSlider);

class AccordionElement extends HTMLElement {
  connectedCallback() {
    // Moving a connected accordion (e.g. <cart-recommendations>) reconnects it; bind once.
    if (this._initialized) return;
    this._initialized = true;
    this.toggle = this.querySelector('.accordion_toggle');
    this.content = this.querySelector('.accordion_content');
    this._forceOpenQuery = this.dataset.forceOpenMedia ? window.matchMedia(this.dataset.forceOpenMedia) : null;

    // data-preopen opens with no animation, even when measured inside a hidden ancestor.
    this._setState(this.classList.contains('open'), this.hasAttribute('data-preopen'));
    this.toggle.addEventListener('click', this._handleToggleClick.bind(this));
    if (this._forceOpenQuery) this._forceOpenQuery.addEventListener('change', () => this._setState(this.classList.contains('open')));
  }

  _isForcedOpen() {
    return Boolean(this._forceOpenQuery && this._forceOpenQuery.matches);
  }

  _closeSiblings() {
    if (!this.parentElement) return;
    Array.from(this.parentElement.children).forEach((sibling) => {
      if (sibling !== this && sibling.tagName === 'ACCORDION-ELEMENT') sibling.close();
    });
  }

  _handleToggleClick() {
    if (this._isForcedOpen()) return;

    const isOpen = this.classList.contains('open');
    if (!isOpen) {
      this._closeSiblings();
      this.dispatchEvent(new CustomEvent('accordion:open', { bubbles: true }));
    }

    this.classList.toggle('open', !isOpen);
    this._setState(!isOpen);
  }

  open() {
    if (this.classList.contains('open')) return;
    this.classList.add('open');
    this._setState(true);
  }

  close(instant = false) {
    if (!this.classList.contains('open')) return;
    this.classList.remove('open');
    this._setState(false, instant);
  }

  refreshHeight() {
    // Corrects a 0 measurement from a hidden ancestor, so always instant.
    if (this.classList.contains('open')) this._setState(true, true);
  }

  _setState(isOpen, instant = false) {
    const forcedOpen = this._isForcedOpen();
    this.toggle.setAttribute('aria-expanded', (isOpen || forcedOpen).toString());

    if (isOpen || forcedOpen) {
      this.content.removeAttribute('inert');
      if (!forcedOpen) this._setMaxHeight(`${this.content.scrollHeight}px`, instant);
    } else {
      this.content.setAttribute('inert', '');
      this._setMaxHeight('0', instant);
    }
  }

  // instant suspends the max-height transition for this one assignment.
  _setMaxHeight(value, instant) {
    if (!instant) {
      this.content.style.maxHeight = value;
      return;
    }
    const previousTransition = this.content.style.transition;
    this.content.style.transition = 'none';
    this.content.style.maxHeight = value;
    void this.content.offsetHeight;
    this.content.style.transition = previousTransition;
  }
}
customElements.define('accordion-element', AccordionElement);

class MediaBlock extends HTMLElement {
  connectedCallback() {
    const videos = this.querySelectorAll('video');
    this.desktopVideo = videos[0] || null;
    this.mobileVideo = videos[1] || null;
    this.mediaQuery = window.matchMedia('(min-width: 768px)');
    
    this.targetPlaying = this.dataset.autoplay !== 'false';
    this.addEventListener('media-controls:toggle', (event) => {
      this.targetPlaying = event.detail.playing;
    });

    if (this.desktopVideo || this.mobileVideo) {
      this.observer = new IntersectionObserver(
        (entries) => this.handleVisibility(entries[0].isIntersecting),
        { threshold: 0.5 }
      );
      this.observer.observe(this);
    }
  }

  handleVisibility(isVisible) {
    this.pauseAll();
    if (!isVisible || !this.targetPlaying) return;

    const videoToPlay = this.desktopVideo && this.mobileVideo
      ? (this.mediaQuery.matches ? this.desktopVideo : this.mobileVideo)
      : (this.desktopVideo || this.mobileVideo);

    videoToPlay?.play();
  }

  pauseAll() {
    this.desktopVideo?.pause();
    this.mobileVideo?.pause();
  }
}
customElements.define('media-block', MediaBlock);

class MediaControls extends HTMLElement {
  connectedCallback() {
    this.mediaContainer = this.closest('.video-banner');
    this.button = this.querySelector('.media-toggle');
    if (!this.mediaContainer || !this.button) return;

    this.button.addEventListener('click', () => this.toggleActiveVideo());
    this.mediaContainer.querySelectorAll('video').forEach((video) => {
      video.addEventListener('play', () => this.syncButton(true));
      video.addEventListener('pause', () => this.syncButton(false));
    });
  }

  getActiveVideo() {
    const videos = this.mediaContainer.querySelectorAll('video');
    return videos.length > 1 && window.matchMedia('(max-width: 768px)').matches ? videos[1] : videos[0];
  }

  toggleActiveVideo() {
    const video = this.getActiveVideo();
    if (!video) return;
    const willPlay = video.paused;
    willPlay ? video.play() : video.pause();
    this.dispatchEvent(new CustomEvent('media-controls:toggle', { bubbles: true, detail: { playing: willPlay } }));
  }

  syncButton(isPlaying) {
    this.button.classList.toggle('paused', !isPlaying);
    this.button.setAttribute('aria-label', isPlaying ? this.button.dataset.pauseLabel : this.button.dataset.playLabel);
  }
}
customElements.define('media-controls', MediaControls);

class ChatLauncher extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('.chat-launcher_button');
    if (!this.button) return;

    // Keep the launcher off the footer's links
    const footer = document.querySelector('.footer-content');
    if (footer) {
      new IntersectionObserver(([entry]) => this.classList.toggle('is-footer-visible', entry.isIntersecting)).observe(
        footer
      );
    }

    const ready = window.GorgiasChat
      ? window.GorgiasChat.init()
      : new Promise((resolve) => window.addEventListener('gorgias-widget-loaded', resolve, { once: true }));

    ready.then(() => {
      const chat = window.GorgiasChat;
      if (!chat) return;
      this.button.addEventListener('click', () => chat.open());
      // Clicks inside the chat iframe never reach this document, so any pointerdown here is outside it.
      document.addEventListener('pointerdown', (event) => {
        if (chat.isOpen() && !event.target.closest('#gorgias-chat-container, chat-launcher')) chat.close();
      });
      chat.on('widget:opened', () => this.syncOpen(true));
      chat.on('widget:closed', () => this.syncOpen(false));
      this.syncOpen(chat.isOpen());
      this.hidden = false;
      this.watchChatWindow();
    });
  }

  syncOpen(isOpen) {
    this.classList.toggle('is-chat-open', isOpen);
    this.button.setAttribute('aria-expanded', String(isOpen));
  }

  // base.css keeps #chat-window invisible until data-theme-state is "done", so it never shows Gorgias's own styles.
  watchChatWindow() {
    const container = document.getElementById('gorgias-chat-container');
    if (!container) return;
    new MutationObserver(() => {
      if (container.querySelector('#chat-window:not([data-theme-state])')) this.styleChatWindow();
    }).observe(container, { childList: true, subtree: true });
    this.styleChatWindow();
  }

  // The iframe's srcdoc replaces its blank document after creation, so wait for Gorgias's mount node.
  styleChatWindow(attempt = 0) {
    const frame = document.querySelector('#gorgias-chat-container #chat-window');
    if (!frame) return;
    if (attempt === 0) {
      if (frame.dataset.themeState) return;
      frame.dataset.themeState = 'pending';
      if (!frame.dataset.themeListener) {
        frame.dataset.themeListener = 'true';
        frame.addEventListener('load', () => {
          delete frame.dataset.themeState;
          this.styleChatWindow();
        });
      }
    }

    const done = () => (frame.dataset.themeState = 'done');
    const doc = frame.contentDocument;
    if (!doc?.head || !doc.querySelector('.frame-root')) {
      if (attempt < 100) setTimeout(() => this.styleChatWindow(attempt + 1), 50);
      else done();
      return;
    }
    const existing = doc.getElementById('chat-launcher-theme');
    if (existing) {
      if (existing.sheet) done();
      return;
    }

    doc.documentElement.classList.add('chat-window-theme');

    // Same @font-face rules (and font URLs) as the page, so the fonts come from cache.
    const fontFaces = doc.createElement('style');
    fontFaces.textContent = [...document.styleSheets]
      .flatMap((sheet) => {
        try {
          return [...sheet.cssRules];
        } catch {
          return [];
        }
      })
      .filter(
        (rule) =>
          rule instanceof CSSFontFaceRule &&
          ['saans-regular', 'abc-synt-regular'].includes(rule.style.getPropertyValue('font-family').replace(/["']/g, ''))
      )
      .map((rule) => rule.cssText)
      .join('\n');
    doc.head.appendChild(fontFaces);

    const root = getComputedStyle(document.documentElement);
    ['--color-black', '--color-white', '--color-off-white', '--color-grey-1', '--color-grey-2', '--color-grey-4'].forEach(
      (token) => doc.documentElement.style.setProperty(token, root.getPropertyValue(token))
    );

    const link = doc.createElement('link');
    link.id = 'chat-launcher-theme';
    link.rel = 'stylesheet';
    link.href = this.dataset.windowCss;
    // fonts.ready resolves at once if layout hasn't requested the fonts yet, which revealed the fallback font.
    link.addEventListener('load', () =>
      Promise.all([doc.fonts.load("1em 'saans-regular'"), doc.fonts.load("1em 'abc-synt-regular'")]).finally(done)
    );
    link.addEventListener('error', done);
    doc.head.appendChild(link);

    this.markShopperBubbles(doc);
    new MutationObserver(() => this.markShopperBubbles(doc)).observe(doc.body, { childList: true, subtree: true });
  }

  // Only the shopper's messages get a bubble from Gorgias (a background image); its markup doesn't tell them apart.
  markShopperBubbles(doc) {
    doc.querySelectorAll('#conversation article:not([data-bubble-checked])').forEach((article) => {
      const message = article.querySelector('[id^="message-"][id$="-content"], p[id^="message-"]');
      if (!message) return;
      article.dataset.bubbleChecked = 'true';
      const bubble = message.parentElement;
      if (doc.defaultView.getComputedStyle(bubble).backgroundImage !== 'none') {
        bubble.classList.add('chat-window-theme_bubble');
      }
    });
  }
}
customElements.define('chat-launcher', ChatLauncher);

class OurMaterialsSlider extends HTMLElement {
  connectedCallback() {
    if (typeof Swiper === 'undefined') return;

    this.swiper = new Swiper(this, {
      slidesPerView: 1.3,
      spaceBetween: 10,
      watchOverflow: true,
      grabCursor: true,
      freeMode: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      breakpoints: {
        769: {
          spaceBetween: 16,
          slidesPerView: 2.2,
        },
        1025: {
          spaceBetween: 16,
          slidesPerView: 3,
        },
      },
    });
  }

  disconnectedCallback() {
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

customElements.define('our-materials-slider', OurMaterialsSlider);


 class MediaSliderCarousel extends HTMLElement {
    connectedCallback() {
      if (typeof Swiper === 'undefined') return;

      const wrapper = this.closest('.media_slider');
      const header = wrapper ? wrapper.querySelector('.media_slider_header') : null;
      const counter = header ? header.querySelector('.media_slider_counter') : null;
      const prevBtn = header ? header.querySelector('.media_slider_nav_prev') : null;
      const nextBtn = header ? header.querySelector('.media_slider_nav_next') : null;

      const desktopPerView = parseFloat(this.dataset.slidesPerView) || 3;
      const mobilePerView = parseFloat(this.dataset.slidesPerViewMobile) || 1.2;

      const swiperOptions = {
        slidesPerView: mobilePerView,
        spaceBetween: 10,
        watchOverflow: true,
        grabCursor: true,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true,
        },
        breakpoints: {
          769: {
            slidesPerView: desktopPerView,
            spaceBetween: 16,
          },
        },
      };

      if (counter) {
        swiperOptions.pagination = {
          el: counter,
          type: 'fraction',
        };
      }

      if (prevBtn && nextBtn) {
        swiperOptions.navigation = {
          prevEl: prevBtn,
          nextEl: nextBtn,
        };
      }

      this.swiper = new Swiper(this, swiperOptions);
    }

    disconnectedCallback() {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
    }
  }

  customElements.define('media-slider-carousel', MediaSliderCarousel);